import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Plus, Printer, RotateCcw, Trash2 } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  amountOff,
  applyVat,
  percentOff,
  reverseDiscount,
  stackedDiscount,
} from "@/lib/calculators/discount";

type B = { en: string; bn: string };
type Mode = "percent" | "amount" | "reverse" | "stack";

const T = {
  h1: { en: "Discount Calculator", bn: "ডিসকাউন্ট ক্যালকুলেটর" },
  desc: {
    en: "Calculate sale price and savings — percent off, fixed amount off, reverse, or stacked discounts. Perfect for Eid, Pohela Boishakh and Daraz/Othoba flash sales.",
    bn: "ডিসকাউন্টের পর মূল্য ও সাশ্রয় হিসাব করুন — % ছাড়, ৳ ছাড়, রিভার্স বা স্ট্যাকড। ঈদ, পহেলা বৈশাখ ও দারাজ/অথবা সেলের জন্য আদর্শ।",
  },
  mode: { en: "Mode", bn: "ধরন" },
  percent: { en: "% off", bn: "% ছাড়" },
  amount: { en: "৳ off", bn: "৳ ছাড়" },
  reverse: { en: "Reverse (find %)", bn: "রিভার্স (% বের করুন)" },
  stack: { en: "Stack discounts", bn: "স্ট্যাক করুন" },

  original: { en: "Original price", bn: "মূল মূল্য" },
  discountPct: { en: "Discount %", bn: "ছাড় %" },
  discountAmt: { en: "Discount amount", bn: "ছাড়ের পরিমাণ" },
  finalPrice: { en: "Final price", bn: "চূড়ান্ত মূল্য" },

  save: { en: "You save", bn: "সাশ্রয়" },
  pay: { en: "You pay", bn: "প্রদেয়" },
  effective: { en: "Effective discount", bn: "কার্যকর ছাড়" },

  addLayer: { en: "Add discount layer", bn: "নতুন স্তর যোগ" },
  layer: { en: "Layer", bn: "স্তর" },
  before: { en: "Before", bn: "আগে" },
  saved: { en: "Saved", bn: "সাশ্রয়" },
  after: { en: "After", bn: "পরে" },
  stackNote: {
    en: "Stacked discounts apply sequentially — 30% + 20% gives 44% off, not 50%.",
    bn: "স্ট্যাকড ছাড় ক্রমান্বয়ে প্রয়োগ হয় — ৩০% + ২০% = ৪৪% ছাড়, ৫০% নয়।",
  },

  addVat: { en: "Add VAT after discount", bn: "ডিসকাউন্টের পর VAT যোগ" },
  vatRate: { en: "VAT rate (%)", bn: "VAT হার (%)" },
  roundCash: { en: "Round to nearest ৳", bn: "নিকটতম ৳ এ রাউন্ড" },

  receipt: { en: "Receipt", bn: "রসিদ" },
  print: { en: "Print", bn: "প্রিন্ট" },
  reset: { en: "Reset", bn: "রিসেট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How to calculate sale price?", bn: "সেল মূল্য কীভাবে গণনা করা হয়?" },
    a: {
      en: "Multiply the original price by the discount percentage and subtract. Example: ৳1,000 at 30% off → discount = 1000 × 30/100 = ৳300, sale price = ৳700.",
      bn: "মূল মূল্যকে ছাড়ের শতাংশ দিয়ে গুণ করে বিয়োগ করুন। উদাহরণ: ৳১,০০০ এ ৩০% ছাড় → ছাড় = ১০০০ × ৩০/১০০ = ৳৩০০, সেল মূল্য = ৳৭০০।",
    },
  },
  {
    q: { en: "Are stacked discounts more than the sum?", bn: "স্ট্যাকড ছাড় কি যোগফলের সমান?" },
    a: {
      en: "No. Stacked discounts apply sequentially: 30% then 20% on ৳1,000 → ৳700 → ৳560, total saved ৳440 (44%), not 50%. The second discount applies to the already-discounted price, so you save less than the simple sum.",
      bn: "না। স্ট্যাকড ছাড় ক্রমান্বয়ে হয়: ৩০% এরপর ২০% ৳১,০০০ এ → ৳৭০০ → ৳৫৬০, মোট সাশ্রয় ৳৪৪০ (৪৪%), ৫০% নয়। দ্বিতীয় ছাড় ইতিমধ্যে কমানো মূল্যের উপর হয়, তাই যোগফলের চেয়ে কম সাশ্রয় হয়।",
    },
  },
  {
    q: { en: "ডিসকাউন্ট কীভাবে গণনা করা হয়?", bn: "ডিসকাউন্ট কীভাবে গণনা করা হয়?" },
    a: {
      en: "ছাড় = মূল মূল্য × (ছাড় % / ১০০)। চূড়ান্ত মূল্য = মূল মূল্য − ছাড়। উপরের ক্যালকুলেটরে মান বসিয়ে সাথে সাথে ফলাফল দেখুন।",
      bn: "ছাড় = মূল মূল্য × (ছাড় % / ১০০)। চূড়ান্ত মূল্য = মূল মূল্য − ছাড়। উপরের ক্যালকুলেটরে মান বসিয়ে সাথে সাথে ফলাফল দেখুন।",
    },
  },
  {
    q: { en: "Is VAT applied before or after discount?", bn: "ছাড়ের আগে না পরে VAT?" },
    a: {
      en: "In Bangladesh, VAT is generally calculated on the actual transaction value — i.e., the price after discount. So discount first, then add 15% VAT. The 'Add VAT after discount' option above does exactly this.",
      bn: "বাংলাদেশে VAT সাধারণত প্রকৃত লেনদেন মূল্যে — অর্থাৎ ছাড়ের পরের মূল্যে — হয়। তাই প্রথমে ছাড়, এরপর ১৫% VAT। উপরের 'ছাড়ের পর VAT যোগ' অপশন ঠিক এটাই করে।",
    },
  },
  {
    q: { en: "How do I find the discount % from old and new prices?", bn: "পুরাতন ও নতুন মূল্য থেকে ছাড় % কীভাবে বের করব?" },
    a: {
      en: "Use the Reverse mode: % = ((Original − Final) ÷ Original) × 100. Example: ৳1,000 → ৳750 means (250/1000)×100 = 25% off.",
      bn: "রিভার্স মোড ব্যবহার করুন: % = ((মূল − চূড়ান্ত) ÷ মূল) × ১০০। উদাহরণ: ৳১,০০০ → ৳৭৫০ মানে (২৫০/১০০০)×১০০ = ২৫% ছাড়।",
    },
  },
  {
    q: { en: "Tips for comparing online prices in BD?", bn: "BD তে অনলাইন মূল্য তুলনার টিপস?" },
    a: {
      en: "Always check the price after discount and after delivery charge. Many sites (Daraz, Othoba, Pickaboo, Chaldal) list a 'crossed-out' MRP that may be inflated. Use this calculator's Reverse mode to verify the real % off claimed.",
      bn: "ডেলিভারি চার্জসহ চূড়ান্ত মূল্য দেখুন। অনেক সাইটে (দারাজ, অথবা, পিকাবু, চালডাল) দেখানো MRP বাড়ানো হতে পারে। প্রকৃত % ছাড় যাচাই করতে রিভার্স মোড ব্যবহার করুন।",
    },
  },
];

export const Route = createFileRoute("/discount-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Discount Calculator — Sale Price & Savings",
      description:
        "Free discount calculator. Calculate sale price after % off or amount off. Stack multiple discounts. Perfect for Eid sale, Pohela Boishakh shopping. ডিসকাউন্ট ক্যালকুলেটর।",
      path: "/discount-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Discount Calculator", path: "/discount-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: DiscountCalculatorPage,
});

function DiscountCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const [mode, setMode] = useState<Mode>("percent");
  const [originalStr, setOriginalStr] = useState("1000");
  const [pctStr, setPctStr] = useState("30");
  const [amtStr, setAmtStr] = useState("150");
  const [finalStr, setFinalStr] = useState("750");
  const [layers, setLayers] = useState<string[]>(["30", "20"]);

  const [vatOn, setVatOn] = useState(false);
  const [vatStr, setVatStr] = useState("15");
  const [roundCash, setRoundCash] = useState(false);

  const original = parseFloat(originalStr) || 0;

  const base = useMemo(() => {
    switch (mode) {
      case "percent": return percentOff(original, parseFloat(pctStr) || 0);
      case "amount": return amountOff(original, parseFloat(amtStr) || 0);
      case "reverse": return reverseDiscount(original, parseFloat(finalStr) || 0);
      case "stack": return stackedDiscount(original, layers.map((l) => parseFloat(l) || 0));
    }
  }, [mode, original, pctStr, amtStr, finalStr, layers]);

  const stackResult = mode === "stack" ? (base as ReturnType<typeof stackedDiscount>) : null;

  const vatPct = parseFloat(vatStr) || 0;
  const vat = vatOn ? applyVat(base.finalPrice, vatPct) : null;

  let payable = vat ? vat.gross : base.finalPrice;
  if (roundCash) payable = Math.round(payable);

  const useBn = lang === "bn" || bnDigits;
  const money = (v: number, frac = 2) => formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));

  const reset = () => {
    setMode("percent");
    setOriginalStr("1000");
    setPctStr("30");
    setAmtStr("150");
    setFinalStr("750");
    setLayers(["30", "20"]);
    setVatOn(false);
    setVatStr("15");
    setRoundCash(false);
  };

  const updateLayer = (i: number, v: string) =>
    setLayers((arr) => arr.map((x, idx) => (idx === i ? v : x)));
  const removeLayer = (i: number) => setLayers((arr) => arr.filter((_, idx) => idx !== i));
  const addLayer = () => setLayers((arr) => [...arr, "10"]);

  const MODES: { value: Mode; label: string }[] = [
    { value: "percent", label: tr("percent") },
    { value: "amount", label: tr("amount") },
    { value: "reverse", label: tr("reverse") },
    { value: "stack", label: tr("stack") },
  ];

  return (
    <CalculatorLayout
      slug="discount-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Tag"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Mode chips */}
      <div className="space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("mode")}</p>
        <div className="flex flex-wrap gap-1.5">
          {MODES.map((m) => {
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs */}
      <div className="mt-6 space-y-4">
        <NumberInput label={tr("original")} value={originalStr} onChange={setOriginalStr} unit="৳" />

        {mode === "percent" && (
          <NumberInput label={tr("discountPct")} value={pctStr} onChange={setPctStr} unit="%" />
        )}
        {mode === "amount" && (
          <NumberInput label={tr("discountAmt")} value={amtStr} onChange={setAmtStr} unit="৳" />
        )}
        {mode === "reverse" && (
          <NumberInput label={tr("finalPrice")} value={finalStr} onChange={setFinalStr} unit="৳" />
        )}
        {mode === "stack" && (
          <div className="space-y-2">
            <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("stackNote")}</p>
            <div className="space-y-2">
              {layers.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={cn("w-12 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {tr("layer")} {num(i + 1)}
                  </span>
                  <input
                    type="number"
                    value={l}
                    min={0}
                    max={100}
                    step={0.5}
                    onChange={(e) => updateLayer(i, e.target.value)}
                    className="w-24 rounded-md border border-border bg-background px-2 py-1.5 text-right text-sm tabular-nums"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                  {layers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLayer(i)}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addLayer} className="gap-1.5">
              <Plus size={14} /> {tr("addLayer")}
            </Button>
          </div>
        )}
      </div>

      {/* Add-ons */}
      <div className="mt-6 space-y-3 rounded-lg border border-border bg-card/50 p-3">
        <label className={cn("flex items-center gap-2 text-sm", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={vatOn} onChange={(e) => setVatOn(e.target.checked)} className="h-4 w-4 accent-accent" />
          {tr("addVat")}
        </label>
        {vatOn && (
          <div className="ml-6 max-w-[160px]">
            <NumberInput label={tr("vatRate")} value={vatStr} onChange={setVatStr} unit="%" />
          </div>
        )}
        <label className={cn("flex items-center gap-2 text-sm", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={roundCash} onChange={(e) => setRoundCash(e.target.checked)} className="h-4 w-4 accent-accent" />
          {tr("roundCash")}
        </label>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="gap-2"><Printer size={14} /> {tr("print")}</Button>
          <Button variant="outline" onClick={reset} className="gap-2"><RotateCcw size={14} /> {tr("reset")}</Button>
        </div>
      </div>

      {/* Results */}
      <motion.div
        id="discount-results"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 space-y-6"
      >
        <div className="grid gap-3 md:grid-cols-3">
          <ResultCard label={tr("original")} value={money(base.original)} />
          <ResultCard label={tr("save")} value={money(base.discount)} accent />
          <ResultCard label={tr("pay")} value={money(payable)} highlight />
        </div>

        <p className={cn("text-center text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("effective")}: <strong className="text-accent">{num(base.effectivePct.toFixed(1))}%</strong>
        </p>

        {/* Stacked breakdown */}
        {stackResult && stackResult.steps.length > 0 && (
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
              <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
                {lang === "en" ? "Step-by-step breakdown" : "ধাপে ধাপে বিভাজন"}
              </h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("layer")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>%</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("before")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("saved")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("after")}</th>
                </tr>
              </thead>
              <tbody>
                {stackResult.steps.map((s, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 tabular-nums">{num(i + 1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(s.pct)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(s.before)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-accent">{money(s.saved)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(s.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Receipt */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className={cn("mb-3 text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("receipt")}</h3>
          <div className="rounded-md bg-muted/40 p-4 font-mono text-sm tabular-nums">
            <div className="flex justify-between">
              <span>{tr("original")}</span>
              <span>{money(base.original)}</span>
            </div>
            <div className="flex justify-between text-accent">
              <span>− {tr("save")} ({num(base.effectivePct.toFixed(1))}%)</span>
              <span>−{money(base.discount)}</span>
            </div>
            <div className="my-1 border-t border-dashed border-border" />
            <div className="flex justify-between">
              <span>{lang === "en" ? "After discount" : "ছাড়ের পর"}</span>
              <span>{money(base.finalPrice)}</span>
            </div>
            {vat && (
              <div className="flex justify-between">
                <span>+ {lang === "en" ? "VAT" : "ভ্যাট"} ({num(vatPct)}%)</span>
                <span>{money(vat.vat)}</span>
              </div>
            )}
            <div className="my-2 border-t border-dashed border-border" />
            <div className="flex justify-between text-base font-semibold">
              <span>{tr("pay")}</span>
              <span>{money(payable, roundCash ? 0 : 2)}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How to calculate a discount" : "ডিসকাউন্ট কীভাবে গণনা হয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <strong>Discount = Original × (% / 100)</strong> &nbsp;|&nbsp; <strong>Final = Original − Discount</strong>
            <br />
            {lang === "en"
              ? "Example: ৳1,000 at 30% off → discount ৳300, final price ৳700. To go the other way (you know the sale price and want the %), use Reverse mode."
              : "উদাহরণ: ৳১,০০০ এ ৩০% ছাড় → ছাড় ৳৩০০, চূড়ান্ত ৳৭০০। যদি সেল মূল্য জানেন ও % বের করতে চান — রিভার্স মোড ব্যবহার করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Stacked discounts: 30% + 20% ≠ 50%" : "স্ট্যাকড ছাড়: ৩০% + ২০% ≠ ৫০%"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "When two discounts apply one after the other (common in flash sales: 'extra 20% off already discounted'), the second discount works on the lower price, not the original. So 30% then 20% on ৳1,000 saves ৳440 (44%), not ৳500 (50%)."
              : "দুটি ছাড় ক্রমান্বয়ে প্রয়োগ হলে (ফ্ল্যাশ সেলে 'অতিরিক্ত ২০% ছাড়') দ্বিতীয় ছাড় কম মূল্যের উপর হয়। তাই ৳১,০০০ এ ৩০% এরপর ২০% সাশ্রয় করে ৳৪৪০ (৪৪%), ৳৫০০ (৫০%) নয়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "VAT after discount" : "ছাড়ের পর VAT"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladesh VAT is computed on the actual transaction value — i.e., after the discount. Toggle 'Add VAT after discount' to see the gross price you'll actually pay at the counter."
              : "বাংলাদেশে VAT প্রকৃত লেনদেন মূল্যে — ছাড়ের পরে — হিসাব হয়। 'ছাড়ের পর VAT যোগ' চালু করে কাউন্টারে প্রদেয় চূড়ান্ত মূল্য দেখুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Smart shopping tips for BD sales" : "বাংলাদেশের সেলে স্মার্ট শপিং"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Always include delivery charge before comparing 'You pay'." : "তুলনার আগে ডেলিভারি চার্জ যোগ করুন।"}</li>
            <li>{lang === "en" ? "Daraz, Othoba, Pickaboo, Chaldal sometimes inflate MRP — use Reverse mode to verify the real % off." : "দারাজ, অথবা, পিকাবু, চালডালে কখনো MRP বাড়ানো থাকে — রিভার্স মোডে যাচাই করুন।"}</li>
            <li>{lang === "en" ? "Bank/card EMI 'no cost' isn't always free — check processing fees." : "ব্যাংক/কার্ড EMI 'no cost' সবসময় ফ্রি নয় — প্রসেসিং ফি দেখুন।"}</li>
            <li>{lang === "en" ? "For Eid / Pohela Boishakh sales, lock the date — many brands raise base prices a week before the sale." : "ঈদ/পহেলা বৈশাখ সেলের আগে অনেক ব্র্যান্ড দাম বাড়ায় — তারিখ মাথায় রাখুন।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/vat-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">VAT</Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Percentage" : "শতকরা"}</Link>
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Income Tax" : "আয়কর"}</Link>
          </div>
        </Section>
      </article>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #discount-results, #discount-results * { visibility: visible !important; }
          #discount-results { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </CalculatorLayout>
  );
}

function ResultCard({ label, value, accent, highlight }: { label: string; value: string; accent?: boolean; highlight?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        highlight ? "border-accent bg-accent/10" : accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent || highlight ? "text-accent" : "text-muted-foreground")}>{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
